import React, { useEffect, useState } from "react";
import { Badge, Button, Calendar, Modal, Spin, Typography } from "antd";
import { MailOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../firebase-config";
import { buildGmailComposeUrl, MANAGEMENT_EMAIL } from "../utils/mail";
import XpRewardSection, { parseXpReward } from "./XpRewardSection";
import InscripcionButton from "./InscripcionButton";

interface EventItem {
  id: string;
  title: string;
  date?: string;
  description?: string;
  imagen?: string;
  exp?: number;
  expDetalle?: string;
  inscripcion?: boolean;
}

interface NormalizedEventItem extends EventItem {
  parsedDate: Dayjs | null;
}

// Convierte el campo `fecha` de Firestore (Timestamp | {seconds} | string)
// a un ISO string que dayjs pueda parsear.
const parseFecha = (fecha: unknown): string | undefined => {
  if (!fecha) return undefined;

  if (typeof fecha === "string") {
    return fecha;
  }

  if (typeof fecha === "object") {
    const value = fecha as { toDate?: () => Date; seconds?: number };
    if (typeof value.toDate === "function") {
      return value.toDate().toISOString();
    }
    if (typeof value.seconds === "number") {
      return new Date(value.seconds * 1000).toISOString();
    }
  }

  return undefined;
};

// Convierte URLs dentro de un texto en enlaces clicables.
const renderTextWithLinks = (text: string) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, index) =>
    urlRegex.test(part) ? (
      <a
        key={index}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#7c5cfc", fontWeight: 700, wordBreak: "break-all" }}
      >
        {part}
      </a>
    ) : (
      <React.Fragment key={index}>{part}</React.Fragment>
    ),
  );
};

const EventCalendar: React.FC = () => {
  const [events, setEvents] = useState<EventItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] =
    useState<NormalizedEventItem | null>(null);

  // Suscripción en tiempo real a la colección "Eventos" de Firestore.
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "Eventos"),
      (snapshot) => {
        const data: EventItem[] = snapshot.docs.map((docSnap) => {
          const d = docSnap.data();
          const descripcion = d.descripcion || d.Descripcion || "Evento";
          return {
            id: docSnap.id,
            title: descripcion,
            date: parseFecha(d.fecha ?? d.Fecha),
            description: d.descripcion || d.Descripcion || "",
            imagen: d.imagen || d.Imagen || "",
            // XP opcional (06/10/2026): 0 si el evento no suma XP.
            exp: parseXpReward(d.exp ?? d.Exp),
            expDetalle: d.expDetalle || d.ExpDetalle || "",
            // Inscripción opcional (11/10/2026): muestra el botón "Inscribirme".
            inscripcion: d.inscripcion === true || d.Inscripcion === true,
          };
        });
        setEvents(data);
        setLoading(false);
      },
      (error) => {
        console.error("Error al leer eventos:", error);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, []);

  const normalizedEvents: NormalizedEventItem[] = events
    .map((event) => ({
      ...event,
      parsedDate: event.date ? dayjs(event.date) : null,
    }))
    .sort((a, b) => {
      if (!a.parsedDate) return 1;
      if (!b.parsedDate) return -1;
      return a.parsedDate.valueOf() - b.parsedDate.valueOf();
    });

  // Al hacer clic en un día del calendario, busca un evento en esa fecha
  // y abre el popup si lo encuentra.
  const handleSelectDate = (date: Dayjs) => {
    const eventOnDay = normalizedEvents.find((event) =>
      event.parsedDate?.isSame(date, "day"),
    );
    if (eventOnDay) {
      setSelectedEvent(eventOnDay);
    }
  };

  const handleCloseModal = () => {
    setSelectedEvent(null);
  };

  // Marca con un punto los días que tienen al menos un evento cargado.
  // OJO: lo que devuelve cellRender se AGREGA dentro de la celda (debajo del
  // número), no la reemplaza. Por eso NO devolvemos info.originNode (eso
  // duplicaría el número del día): solo devolvemos el punto cuando hay evento
  // y null en el resto de los casos.
  const cellRender = (
    date: Dayjs,
    info: { type: string; originNode: React.ReactNode },
  ) => {
    if (info.type !== "date") return info.originNode;

    const hasEvent = normalizedEvents.some((event) =>
      event.parsedDate?.isSame(date, "day"),
    );

    if (!hasEvent) return null;

    return (
      <div style={{ display: "flex", justifyContent: "center", marginTop: 2 }}>
        <Badge status="error" />
      </div>
    );
  };

  return (
    <div style={{ padding: 16 }}>
      <Spin spinning={loading}>
        <Calendar
          fullscreen={false}
          onSelect={handleSelectDate}
          cellRender={cellRender}
        />
      </Spin>

      <Modal
        open={Boolean(selectedEvent)}
        onCancel={handleCloseModal}
        title="📅 Evento"
        styles={{ body: { paddingTop: 12 } }}
        footer={[
          selectedEvent?.parsedDate?.month() === 8 &&
          selectedEvent.parsedDate.date() === 25 ? (
            <Button
              key="subir-foto"
              icon={<MailOutlined />}
              href={buildGmailComposeUrl(
                MANAGEMENT_EMAIL,
                "Subir foto - Evento 25 de septiembre",
              )}
              target="_blank"
              rel="noopener noreferrer"
            >
              Subir Foto
            </Button>
          ) : null,
          <Button key="cerrar" type="primary" onClick={handleCloseModal}>
            Cerrar
          </Button>,
        ]}
      >
        {selectedEvent && (
          <div>
            {selectedEvent.imagen ? (
              <img
                src={selectedEvent.imagen}
                alt="Evento"
                style={{
                  width: "100%",
                  objectFit: "contain",
                  borderRadius: 12,
                  marginBottom: 12,
                }}
              />
            ) : null}

            {selectedEvent.parsedDate ? (
              <Typography.Paragraph
                strong
                style={{
                  color: "var(--dashboard-header-blue)",
                  marginBottom: 12,
                }}
              >
                📆 {selectedEvent.parsedDate.format("dddd D [de] MMMM, YYYY")}
              </Typography.Paragraph>
            ) : null}

            {/* XP opcional: solo se muestra si el evento suma XP */}
            {selectedEvent.exp ? (
              <div style={{ marginBottom: 12 }}>
                <XpRewardSection
                  exp={selectedEvent.exp}
                  detail={selectedEvent.expDetalle}
                />
              </div>
            ) : null}

            <Typography.Paragraph
              style={{ color: "var(--dashboard-text)", marginBottom: 0 }}
            >
              {renderTextWithLinks(selectedEvent.description || "")}
            </Typography.Paragraph>

            {/* Inscripción opcional: solo si el admin la activó */}
            {selectedEvent.inscripcion ? (
              <div style={{ marginTop: 16 }}>
                <InscripcionButton
                  coleccion="Eventos"
                  itemId={selectedEvent.id}
                />
              </div>
            ) : null}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default EventCalendar;
